import { RankingDemoBanner } from "@/components/ranking-demo-banner";
import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getDealDetail, adjustDealPlan } from "@/lib/deals.functions";
import { createRun, getMyLatestSenderInfo, updateDealOutreachInfo } from "@/lib/deals.functions";
import { startPipeline } from "@/lib/run.functions";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Field } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InvestorTypesPicker } from "@/components/investor-types-picker";
import { useAuth } from "@/hooks/use-auth";
import { FALLBACK_DELIVERY_START, safeUserMessage } from "@/lib/user-error";
import { updateDealPlanTags } from "@/lib/deals.functions";
import { updateDealPlanLpTargets } from "@/lib/deals.functions";
import { updateDealPlanLoanTargets } from "@/lib/deals.functions";
import { updateDealPlanReTargets } from "@/lib/deals.functions";
import { getPlanVocabulary, getRun } from "@/lib/run.functions";
import { estimateRunMinutes } from "@/lib/run-estimate";
import { STAGE_NAME } from "@/lib/run-stage";
import { TagPicker } from "@/components/tag-picker";
import { computeTagDiff } from "@/lib/tag-diff";
import { PlanTagSummary, normalizeClientBranch } from "@/components/plan-tag-summary";
import { RaiseSummary } from "@/components/raise-summary";
import { LpTargetSelector } from "@/components/lp-target-selector";
import { computeDefaultLpTargets, type LpTarget } from "@/lib/lp-targets";
import { LoanTargetingLadder } from "@/components/loan-targeting-ladder";
import { LoanTargetSelector } from "@/components/loan-target-selector";
import { ReTargetSelector } from "@/components/re-target-selector";
import { resolveLadder, type LoanTypePickKind } from "@/lib/loan-taxonomy";
import { FileField, type FileFieldValue } from "@/components/file-field";
import { DealClassificationSection } from "@/components/deal-classification-section";
import { DealExcludedDomains } from "@/components/deal-excluded-domains";
import { DealIncludedDomains } from "@/components/deal-included-domains";
import { DealHqLocationSection } from "@/components/deal-hq-location-section";
import { DealNav } from "@/components/deal-nav";
import { DealPageHeader } from "@/components/deal-page-header";

import {
  FIN_PATTERNS,
  findFinancialM,
  interpretedMusdLine,
  normalizeMusdInput,
  toNullableNumber,
} from "@/lib/deal-financials";

import { track } from "@/lib/track";
import { useOnboarding } from "@/lib/onboarding";
import { friendlyRunFailure } from "@/lib/run-failure";

type MaterialCategory = "presentation" | "memo" | "template";
const SLOT_LABELS: Record<MaterialCategory, string> = {
  presentation: "Investor presentation",
  memo: "Memo or supporting materials",
  template: "Template email",
};

export const Route = createFileRoute("/_authed/deals/$dealId_/refine")({
  head: () => ({ meta: [{ title: "Refine deal plan: CapitalDart" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: DealDetailPage,
  errorComponent: DealNotFoundBoundary,
});

function DealNotFoundBoundary() {
  const router = useRouter();
  return (
    <main className="mx-auto max-w-3xl px-6 py-10 space-y-4">
      <h1 className="font-serif text-2xl text-brand-ink">Deal not found</h1>
      <p className="text-sm text-brand-muted">
        This deal doesn&rsquo;t exist or you don&rsquo;t have access to it.
      </p>
      <Button variant="outline" onClick={() => router.navigate({ to: "/dashboard" })}>
        Back to dashboard
      </Button>
    </main>
  );
}

function DealDetailPage() {
  const { dealId } = Route.useParams();
  const qc = useQueryClient();
  const fetchDetail = useServerFn(getDealDetail);
  const getRunFn = useServerFn(getRun);
  const adjustPlanFn = useServerFn(adjustDealPlan);
  const updateDealPlanTagsFn = useServerFn(updateDealPlanTags);
  const updateDealPlanLpTargetsFn = useServerFn(updateDealPlanLpTargets);
  const updateDealPlanLoanTargetsFn = useServerFn(updateDealPlanLoanTargets);
  const updateDealPlanReTargetsFn = useServerFn(updateDealPlanReTargets);
  const vocabFn = useServerFn(getPlanVocabulary);
  const createRunFn = useServerFn(createRun);
  const startPipelineFn = useServerFn(startPipeline);
  const updateOutreachInfoFn = useServerFn(updateDealOutreachInfo);
  const fetchLatestSender = useServerFn(getMyLatestSenderInfo);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState("");
  const [planBusy, setPlanBusy] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [continueAfterSave, setContinueAfterSave] = useState(false);
  // Inline "Get more contacts" panel (replaces the old /app second screen).
  const [contactsPanelOpen, setContactsPanelOpen] = useState(false);
  const [deliveryTarget, setDeliveryTarget] = useState("100");
  const [draftOutreach, setDraftOutreach] = useState(true);
  // Investor targeting + About you — identical fields to the /app builder.
  const [investorTypesRequest, setInvestorTypesRequest] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const typesInputRef = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState("");
  const [role, setRole] = useState("");
  const [firm, setFirm] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const senderInitedRef = useRef(false);
  const [startBusy, setStartBusy] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [presentationFile, setPresentationFile] = useState<FileFieldValue | null>(null);
  const [memoFile, setMemoFile] = useState<FileFieldValue | null>(null);
  const [templateFile, setTemplateFile] = useState<FileFieldValue | null>(null);
  const [removals, setRemovals] = useState<Set<MaterialCategory>>(new Set());
  // Optional company financials ($M). Text state so blank means null and
  // partially typed decimals ("-1.") survive keystrokes.
  const [revenueM, setRevenueM] = useState("");
  const [ebitdaM, setEbitdaM] = useState("");
  const [premoneyM, setPremoneyM] = useState("");
  const [finSeed, setFinSeed] = useState({ revenue: "", ebitda: "", premoney: "" });
  const finInitedRef = useRef(false);

  // Manual tag picker state — DirectEquityMezz only. Seeded once from
  // deals.plan_json and re-seeded after each successful save.
  const [pickIndustryTypes, setPickIndustryTypes] = useState<string[]>([]);
  const [pickCompanyTypes, setPickCompanyTypes] = useState<string[]>([]);
  const [pickVerticalTypes, setPickVerticalTypes] = useState<string[]>([]);
  const [pickStageTypes, setPickStageTypes] = useState<string[]>([]);
  const tagInitedRef = useRef(false);

  // Fund-only LP-target editor state. Seeded once from deals.plan_json
  // (existing lpTargets, else computeDefaultLpTargets) and re-seeded after
  // each successful save.
  const [lpTargetsPick, setLpTargetsPick] = useState<LpTarget[]>([]);
  const [lpSeedIds, setLpSeedIds] = useState<string[]>([]);
  const lpInitedRef = useRef(false);

  // Loans/Debt-only loan-ladder editor state. Seeded once from
  // deals.plan_json (existing loanTargets, else resolveLadder from
  // loanTypePrimary, else empty) and re-seeded after each successful save.
  const [loanChecked, setLoanChecked] = useState<string[]>([]);
  const [loanSeedIds, setLoanSeedIds] = useState<string[]>([]);
  const loanInitedRef = useRef(false);

  // RealEstate-only targeting editor state. Seeded once from
  // deals.plan_json and re-seeded after each successful save.
  const [rePropertyTypes, setRePropertyTypes] = useState<string[]>([]);
  const [reCapitalTypes, setReCapitalTypes] = useState<string[]>([]);
  const [rePropSeed, setRePropSeed] = useState<string[]>([]);
  const [reCapSeed, setReCapSeed] = useState<string[]>([]);
  const reInitedRef = useRef(false);

  const vocabQuery = useQuery({
    queryKey: ["planVocabulary"],
    queryFn: () => vocabFn(),
    staleTime: 1000 * 60 * 60,
  });
  const vocab = vocabQuery.data?.vocab;

  const detail = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDetail({ data: { dealId } }),
    retry: (failureCount) => failureCount < 3,
  });
  const { first_run_completed_at, markSeen } = useOnboarding();
  const firstRunMarkedRef = useRef(false);

  const hasDeliveredContacts =
    (detail.data as { has_delivered_contacts?: boolean } | undefined)?.has_delivered_contacts === true;

  useEffect(() => {
    if (hasDeliveredContacts && first_run_completed_at === null && !firstRunMarkedRef.current) {
      firstRunMarkedRef.current = true;
      markSeen("first_run_completed_at");
    }
  }, [hasDeliveredContacts, first_run_completed_at, markSeen]);

  // Seed pickers from deal.plan_json once, DirectEquityMezz only.
  const planForSeed = (detail.data?.deal as { plan_json?: unknown } | undefined)?.plan_json;
  // Seed targeting + About you from the deal row, then fall back to the
  // user's most recent deal (same source /app prefills from).
  const dealForSender = detail.data?.deal as
    | { sender_info?: unknown; investor_types_request?: string | null }
    | undefined;
  useEffect(() => {
    if (!dealForSender || senderInitedRef.current) return;
    senderInitedRef.current = true;
    const s = (dealForSender.sender_info ?? {}) as Record<string, unknown>;
    if (typeof s.firstName === "string") setFirstName(s.firstName);
    if (typeof s.role === "string") setRole(s.role);
    if (typeof s.firm === "string" && s.firm) setFirm(s.firm);
    if (dealForSender.investor_types_request) {
      setInvestorTypesRequest(dealForSender.investor_types_request);
    }
  }, [dealForSender]);
  // Pre-fill the refine textarea with the stored user priority, once.
  const priorityInitedRef = useRef(false);
  useEffect(() => {
    if (priorityInitedRef.current || !planForSeed) return;
    priorityInitedRef.current = true;
    const p = (planForSeed as { userPriority?: unknown }).userPriority;
    if (typeof p === "string" && p.trim()) setFeedback(p);
  }, [planForSeed]);
  useEffect(() => {
    const email = user?.email;
    if (!email) return;
    setUserEmail((prev) => (prev === "" ? email : prev));
  }, [user?.email]);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    fetchLatestSender()
      .then((info) => {
        if (cancelled || !info) return;
        if (info.firstName) setFirstName((prev) => (prev === "" ? info.firstName! : prev));
        if (info.role) setRole((prev) => (prev === "" ? info.role! : prev));
        if (info.firm) setFirm((prev) => (prev === "" ? info.firm! : prev));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id, fetchLatestSender]);
  // Seed the optional financials once: deals row wins; otherwise fall back to
  // whatever the deck extraction captured in plan_json / deal facts. The
  // fallback is display-only until the user saves.
  const dealForFinancials = detail.data?.deal as
    | {
        revenue_musd?: number | string | null;
        ebitda_musd?: number | string | null;
        target_premoney_musd?: number | string | null;
        plan_json?: unknown;
        deal_context?: unknown;
      }
    | undefined;
  useEffect(() => {
    if (!dealForFinancials || finInitedRef.current) return;
    finInitedRef.current = true;
    const fromRow = (v: unknown) =>
      v === null || v === undefined || v === "" ? "" : String(v);
    const seed = {
      revenue:
        fromRow(dealForFinancials.revenue_musd) ||
        findFinancialM(dealForFinancials.plan_json, FIN_PATTERNS.revenue) ||
        findFinancialM(dealForFinancials.deal_context, FIN_PATTERNS.revenue),
      ebitda:
        fromRow(dealForFinancials.ebitda_musd) ||
        findFinancialM(dealForFinancials.plan_json, FIN_PATTERNS.ebitda) ||
        findFinancialM(dealForFinancials.deal_context, FIN_PATTERNS.ebitda),
      premoney:
        fromRow(dealForFinancials.target_premoney_musd) ||
        findFinancialM(dealForFinancials.plan_json, FIN_PATTERNS.premoney) ||
        findFinancialM(dealForFinancials.deal_context, FIN_PATTERNS.premoney),
    };
    setRevenueM(seed.revenue);
    setEbitdaM(seed.ebitda);
    setPremoneyM(seed.premoney);
    setFinSeed(seed);
  }, [dealForFinancials]);

  useEffect(() => {
    if (!planForSeed) return;
    if (tagInitedRef.current) return;
    const p = planForSeed as Record<string, unknown>;
    if (normalizeClientBranch(typeof p.branch === "string" ? p.branch : undefined) !== "DirectEquityMezz") return;
    tagInitedRef.current = true;
    const toStrArr = (v: unknown): string[] =>
      Array.isArray(v) ? (v as unknown[]).map(String) : [];
    setPickIndustryTypes(toStrArr(p.industryTypes));
    setPickCompanyTypes(toStrArr(p.companyTypes));
    setPickVerticalTypes(toStrArr(p.verticalTypes));
    setPickStageTypes(toStrArr(p.stageTypes));
  }, [planForSeed]);

  // Seed LP targets from deal.plan_json once, Fund only.
  useEffect(() => {
    if (!planForSeed) return;
    if (lpInitedRef.current) return;
    const p = planForSeed as Record<string, unknown>;
    if (normalizeClientBranch(typeof p.branch === "string" ? p.branch : undefined) !== "Fund") return;
    lpInitedRef.current = true;
    const existing = Array.isArray(p.lpTargets) ? (p.lpTargets as unknown[]) : null;
    let seeded: LpTarget[];
    if (existing && existing.length > 0) {
      seeded = existing.map((t, i) => {
        const o = (t ?? {}) as Record<string, unknown>;
        return {
          id: String(o.id ?? ""),
          rank: i + 1,
          label: String(o.label ?? ""),
          appetiteTokens: Array.isArray(o.appetiteTokens)
            ? (o.appetiteTokens as unknown[]).map(String)
            : [],
          archetypeTokens: Array.isArray(o.archetypeTokens)
            ? (o.archetypeTokens as unknown[]).map(String)
            : [],
        };
      });
    } else {
      seeded = computeDefaultLpTargets(p as { dealSummary?: string | null; raiseAmount?: string | null });
    }
    setLpTargetsPick(seeded);
    setLpSeedIds(seeded.map((t) => t.id));
  }, [planForSeed]);

  // Seed loan ladder from deal.plan_json once, Loans/Debt only.
  useEffect(() => {
    if (!planForSeed) return;
    if (loanInitedRef.current) return;
    const p = planForSeed as Record<string, unknown>;
    if (normalizeClientBranch(typeof p.branch === "string" ? p.branch : undefined) !== "Loans/Debt") return;
    loanInitedRef.current = true;
    const existing = Array.isArray(p.loanTargets) ? (p.loanTargets as unknown[]).map(String) : [];
    let seeded: string[];
    if (existing.length > 0) {
      seeded = existing;
    } else if (typeof p.loanTypePrimary === "string" && p.loanTypePrimary) {
      const kind =
        p.loanTypePickKind === "category" ? "category" : ("type" as LoanTypePickKind);
      seeded = resolveLadder(kind, p.loanTypePrimary);
    } else {
      seeded = [];
    }
    setLoanChecked(seeded);
    setLoanSeedIds(seeded);
  }, [planForSeed]);

  // Seed RealEstate targeting from deal.plan_json once, RealEstate only.
  useEffect(() => {
    if (!planForSeed) return;
    if (reInitedRef.current) return;
    const p = planForSeed as Record<string, unknown>;
    if (normalizeClientBranch(typeof p.branch === "string" ? p.branch : undefined) !== "RealEstate") return;
    reInitedRef.current = true;
    const toStrArr = (v: unknown): string[] =>
      Array.isArray(v) ? (v as unknown[]).map(String) : [];
    const props = toStrArr(p.realEstatePropertyTypes);
    const caps = toStrArr(p.realEstateEquityOrDebt);
    setRePropertyTypes(props);
    setReCapitalTypes(caps);
    setRePropSeed(props);
    setReCapSeed(caps);
  }, [planForSeed]);

  // Continue-after-save navigation effect. Must run unconditionally before
  // any early return so hook count stays stable across loading/error/success.
  useEffect(() => {
    if (continueAfterSave && !planBusy) {
      setContinueAfterSave(false);
      if (!planError) {
        setContactsPanelOpen(true);
      }
    }
  }, [continueAfterSave, planBusy, planError, navigate, dealId]);

  // ---- Pipeline-awareness (concern of this change) --------------------
  // Latest pipeline row from the initial fetch. Used to decide whether to
  // poll and to seed the banner's stage on first paint before the first
  // poll returns.
  const seedRun = (detail.data?.runs ?? [])[0] as
    | {
        id: string;
        status: string;
        current_stage: number | null;
        created_at: string;
        delivery_target: number | null;
        final_delivered: number | null;
        error_message: string | null;
      }
    | undefined;
  const IN_FLIGHT = new Set(["planning", "plan_ready", "running"]);
  const TERMINAL = new Set([
    "complete",
    "error",
    "canceled",
    "needs_refinement",
    "failed",
  ]);
  const [liveStage, setLiveStage] = useState<number | null>(null);
  const [completionNote, setCompletionNote] = useState<
    | { kind: "complete"; delivered: number | null }
    | { kind: "failed"; message: string }
    | null
  >(null);
  const [, setNowTick] = useState(0);

  // 1s re-render tick so the elapsed time in the banner updates smoothly
  // while a pipeline is in flight, without waiting on the 2s poll cadence.
  const pollActive = !!seedRun && IN_FLIGHT.has(seedRun.status);
  useEffect(() => {
    if (!pollActive) return;
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [pollActive]);

  // Poll getRun every 2s while non-terminal. On transition to terminal,
  // invalidate the deal-detail query so counts + contact list refresh in
  // place, and record a brief completion note.
  const seedRunId = seedRun?.id;
  useEffect(() => {
    if (!seedRunId || !pollActive) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    async function tick() {
      try {
        const r = (await getRunFn({ data: { runId: seedRunId! } })) as {
          status: string;
          current_stage: number;
          error_message: string | null;
          final_delivered?: number | null;
          final_delivered_new?: number | null;
        };
        if (cancelled) return;
        if (typeof r.current_stage === "number") setLiveStage(r.current_stage);
        if (TERMINAL.has(r.status)) {
          if (r.status === "complete") {
            const delivered =
              typeof r.final_delivered_new === "number"
                ? r.final_delivered_new
                : typeof r.final_delivered === "number"
                  ? r.final_delivered
                  : null;
            setCompletionNote({ kind: "complete", delivered });
            // Auto-clear the note after 5s; by then the refreshed detail
            // has already collapsed the banner because status is terminal.
            setTimeout(() => {
              if (!cancelled) setCompletionNote(null);
            }, 5000);
          } else if (r.status === "error" || r.status === "failed") {
            setCompletionNote({
              kind: "failed",
              message: friendlyRunFailure(r.error_message),
            });
          }
          qc.invalidateQueries({ queryKey: ["deal-detail", dealId] });
          return;
        }
        timer = setTimeout(tick, 2000);
      } catch {
        if (!cancelled) timer = setTimeout(tick, 3000);
      }
    }
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedRunId, pollActive, dealId]);

  // When the seed run changes (new pipeline started, or fresh detail after
  // invalidation), reset local live state so stale UI doesn't leak.
  useEffect(() => {
    setLiveStage(null);
  }, [seedRunId]);

  const renderShell = (children: ReactNode) => (
    <main className="mx-auto max-w-6xl px-6 py-10 space-y-8">
      <DealPageHeader dealId={dealId} />
      <DealNav
        dealId={dealId}
        active="refine"
        isDemo={
          (detail.data?.deal as { is_demo?: boolean } | undefined)?.is_demo ===
          true
        }
      />
      {children}
    </main>
  );

  if (detail.isLoading) {
    return renderShell(
      <div className="mx-auto max-w-3xl px-6 py-10 text-sm text-brand-muted">
        Loading…
      </div>,
    );
  }
  if (detail.isError) {
    return renderShell(
      <div className="mx-auto max-w-3xl space-y-4 rounded-xl border border-brand-line bg-brand-paper px-6 py-10">
        <h1 className="font-serif text-2xl text-brand-ink">Could not load this deal.</h1>
        <Button variant="outline" onClick={() => detail.refetch()}>
          Try again
        </Button>
      </div>,
    );
  }

  if (!detail.data) {
    return renderShell(
      <div className="mx-auto max-w-3xl space-y-4 px-6 py-10">
        <h1 className="font-serif text-2xl text-brand-ink">Deal not found</h1>
        <p className="text-sm text-brand-muted">
          This deal doesn&rsquo;t exist or you don&rsquo;t have access to it.
        </p>
        <Button variant="outline" onClick={() => navigate({ to: "/dashboard" })}>
          Back to dashboard
        </Button>
      </div>,
    );
  }


  const { deal, runs } = detail.data;
  const rankingDemo =
    (detail.data as { ranking_demo?: { category: string; stage: string } | null })
      .ranking_demo ?? null;
  const latestRun = (runs as Array<{
    id: string;
    status: string;
    current_stage: number | null;
    delivery_target: number | null;
    final_delivered: number | null;
    created_at: string;
    error_message: string | null;
    has_download?: boolean;
  }>)[0];
  const pipelineInFlight =
    !!latestRun && IN_FLIGHT.has(latestRun.status);
  const parseReport = (Array.isArray((deal as { parse_report?: unknown }).parse_report)
    ? ((deal as { parse_report: unknown[] }).parse_report as Array<{
        filename: string;
        category: MaterialCategory;
      }>)
    : []
  ).filter((e) => e && (e.category === "presentation" || e.category === "memo" || e.category === "template"));
  const currentBySlot = (cat: MaterialCategory) =>
    parseReport.find((e) => e.category === cat) ?? null;

  const stagedFile = (cat: MaterialCategory): FileFieldValue | null =>
    cat === "presentation" ? presentationFile : cat === "memo" ? memoFile : templateFile;
  const setStagedFile = (cat: MaterialCategory, v: FileFieldValue | null) => {
    if (cat === "presentation") setPresentationFile(v);
    else if (cat === "memo") setMemoFile(v);
    else setTemplateFile(v);
  };
  const toggleRemoval = (cat: MaterialCategory) => {
    setRemovals((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
    // Removing a slot supersedes any staged replacement.
    setStagedFile(cat, null);
  };

  const stagedFiles = [
    presentationFile && { ...presentationFile, category: "presentation" as const },
    memoFile && { ...memoFile, category: "memo" as const },
    templateFile && { ...templateFile, category: "template" as const },
  ].filter((f): f is FileFieldValue & { category: MaterialCategory } => f !== null);
  const removalsList: MaterialCategory[] = Array.from(removals);
  const materialsChanged = stagedFiles.length > 0 || removalsList.length > 0;
  const planJson = (deal as { plan_json?: unknown }).plan_json;
  const branch = normalizeClientBranch(
    typeof (planJson as { branch?: unknown } | undefined)?.branch === "string"
      ? ((planJson as { branch?: string }).branch)
      : undefined,
  );
  const isDirectEquityMezz = branch === "DirectEquityMezz";
  const isFund = branch === "Fund";
  const isLoans = branch === "Loans/Debt";
  const isRealEstate = branch === "RealEstate";
  const lpTargetsDirty = (() => {
    if (!isFund) return false;
    const cur = lpTargetsPick.map((t) => t.id);
    if (cur.length !== lpSeedIds.length) return true;
    for (let i = 0; i < cur.length; i++) {
      if (cur[i] !== lpSeedIds[i]) return true;
    }
    return false;
  })();
  const loanTargetsDirty = (() => {
    if (!isLoans) return false;
    if (loanChecked.length !== loanSeedIds.length) return true;
    for (let i = 0; i < loanChecked.length; i++) {
      if (loanChecked[i] !== loanSeedIds[i]) return true;
    }
    return false;
  })();
  const reDirty = (() => {
    if (!isRealEstate) return false;
    if (rePropertyTypes.length !== rePropSeed.length) return true;
    for (let i = 0; i < rePropertyTypes.length; i++) {
      if (rePropertyTypes[i] !== rePropSeed[i]) return true;
    }
    if (reCapitalTypes.length !== reCapSeed.length) return true;
    for (let i = 0; i < reCapitalTypes.length; i++) {
      if (reCapitalTypes[i] !== reCapSeed[i]) return true;
    }
    return false;
  })();
  const tagDiff = isDirectEquityMezz
    ? computeTagDiff(planJson, {
        industryTypes: pickIndustryTypes,
        companyTypes: pickCompanyTypes,
        verticalTypes: pickVerticalTypes,
        stageTypes: pickStageTypes,
      })
    : {};
  const tagsDirty = Object.keys(tagDiff).length > 0;
  const financialsDirty =
    revenueM.trim() !== finSeed.revenue.trim() ||
    ebitdaM.trim() !== finSeed.ebitda.trim() ||
    premoneyM.trim() !== finSeed.premoney.trim();
  const financialsInvalid =
    toNullableNumber(revenueM) === undefined ||
    toNullableNumber(ebitdaM) === undefined ||
    toNullableNumber(premoneyM) === undefined ||
    (toNullableNumber(revenueM) ?? 0) < 0 ||
    (toNullableNumber(premoneyM) ?? 0) < 0;
  const hasUnsavedChanges =
    feedback.trim().length > 0 ||
    materialsChanged ||
    tagsDirty ||
    lpTargetsDirty ||
    loanTargetsDirty ||
    reDirty ||
    financialsDirty;
  const canSubmit = !planBusy && hasUnsavedChanges && !financialsInvalid;

  async function handleUpdatePlan() {
    setPlanBusy(true);
    setPlanError(null);
    try {
      const needsAdjust = feedback.trim().length > 0 || materialsChanged;
      if (needsAdjust || financialsDirty) {
        await adjustPlanFn({
          data: {
            dealId,
            feedback,
            files: stagedFiles.map((f) => ({
              name: f.name,
              category: f.category,
              base64: f.base64,
            })),
            removals: removalsList,
            ...(financialsDirty
              ? {
                  financials: {
                    revenue_musd: normalizeMusdInput(revenueM),
                    ebitda_musd: normalizeMusdInput(ebitdaM),
                    target_premoney_musd: normalizeMusdInput(premoneyM),

                  },
                }
              : {}),
          },
        });
      }
      if (isDirectEquityMezz && tagsDirty) {
        await updateDealPlanTagsFn({
          data: { dealId, ...tagDiff },
        });
      }
      if (isFund && lpTargetsDirty) {
        await updateDealPlanLpTargetsFn({
          data: { dealId, lpTargets: lpTargetsPick },
        });
      }
      if (isLoans && loanTargetsDirty) {
        await updateDealPlanLoanTargetsFn({
          data: { dealId, loanTargets: loanChecked },
        });
      }
      if (isRealEstate && reDirty) {
        await updateDealPlanReTargetsFn({
          data: {
            dealId,
            realEstatePropertyTypes: rePropertyTypes,
            realEstateEquityOrDebt: reCapitalTypes,
          },
        });
      }
      track(
        "deal_modified",
        {
          feedback_length: feedback.trim().length,
          files_added: stagedFiles.length,
          files_removed: removalsList.length,
        },
        { deal_id: dealId },
      );
      // Keep the text in force visible; empty feedback leaves the stored one in place.
      setFeedback((prev) => prev.trim());
      setPresentationFile(null);
      setMemoFile(null);
      setTemplateFile(null);
      setRemovals(new Set());
      setFinSeed({
        revenue: revenueM.trim(),
        ebitda: ebitdaM.trim(),
        premoney: premoneyM.trim(),
      });
      setRevenueM(revenueM.trim());
      setEbitdaM(ebitdaM.trim());
      setPremoneyM(premoneyM.trim());
      toast.success("Plan updated");
      tagInitedRef.current = false;
      lpInitedRef.current = false;
      loanInitedRef.current = false;
      reInitedRef.current = false;
      qc.invalidateQueries({ queryKey: ["deal-detail", dealId] });
    } catch (e) {
      setPlanError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setPlanBusy(false);
    }
  }

  async function handleContinue() {
    if (planBusy) return;
    if (hasUnsavedChanges) {
      setContinueAfterSave(true);
      await handleUpdatePlan();
    } else {
      setContactsPanelOpen((v) => !v);
    }
  }

  async function handleBuildOutreach() {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.trim());
    if (!firstName.trim()) {
      setStartError("Please enter your first name.");
      return;
    }
    if (!role.trim()) {
      setStartError("Please select your role.");
      return;
    }
    if (!emailValid) {
      setStartError("Please enter a valid email address.");
      return;
    }
    setStartBusy(true);
    setStartError(null);
    try {
      await updateOutreachInfoFn({
        data: {
          dealId,
          investorTypesRequest: investorTypesRequest.trim() || undefined,
          firstName: firstName.trim(),
          role: role.trim(),
          firm: firm.trim() || undefined,
        },
      });
      const r = await createRunFn({
        data: {
          dealId,
          deliveryTarget: Number(deliveryTarget),
          skipOutreach: !draftOutreach,
        },
      });
      const createdRunId = r?.runId ?? r?.run_id;
      if (!createdRunId) {
        throw new Error("Run creation did not return a run id");
      }
      await startPipelineFn({ data: { runId: createdRunId } });
      setContactsPanelOpen(false);
      setCompletionNote(null);
      qc.invalidateQueries({ queryKey: ["deal-detail", dealId] });
      navigate({ to: "/app", search: { runId: createdRunId } });
    } catch (e) {
      console.error("[delivery:start] failed", e);
      setStartError(safeUserMessage(e, FALLBACK_DELIVERY_START));
    } finally {
      setStartBusy(false);
    }
  }


  return renderShell(
    <>
      {rankingDemo && (
        <RankingDemoBanner
          category={rankingDemo.category}
          stage={rankingDemo.stage}
        />
      )}


      {(isDirectEquityMezz || isFund || isLoans || isRealEstate) && (
        <>
        <div className="flex gap-2">
          <Button size="sm" disabled={!canSubmit} onClick={handleUpdatePlan}>
            {planBusy ? "Updating…" : "Update plan"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={planBusy}
            onClick={handleContinue}
          >
            Get more contacts
          </Button>
        </div>
        {contactsPanelOpen && (
          <section className="space-y-4 rounded-xl border border-brand-line bg-brand-paper p-4">
            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="number-of-investors"
                className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
              >
                Number of investors
              </Label>
              <Input
                id="number-of-investors"
                type="number"
                min={10}
                max={1000}
                value={deliveryTarget}
                onChange={(e) => setDeliveryTarget(e.target.value)}
                aria-describedby="number-of-investors-hint"
                className="max-w-[200px] text-[15px]! md:text-[15px]!"
              />
              <p id="number-of-investors-hint" className="text-xs text-muted-foreground">
                10–1000. Default min(100, available credits).
              </p>
            </div>
            <div className="space-y-2 border-t border-brand-line pt-4">
              <div className="flex items-baseline justify-between gap-3">
                <label
                  htmlFor="deal-panel-investor-types"
                  className="block text-xs font-medium uppercase tracking-wider text-muted-foreground"
                >
                  What types of investors are you targeting?{" "}
                  <span className="ml-1 normal-case tracking-normal text-muted-foreground/70">(optional)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Browse types
                </button>
              </div>
              <Input
                id="deal-panel-investor-types"
                ref={typesInputRef}
                value={investorTypesRequest}
                onChange={(e) => setInvestorTypesRequest(e.target.value.slice(0, 500))}
                maxLength={500}
                placeholder='e.g., growth equity, single family offices, angels, specialty lenders. Or "all except SPACs". Or "All".'
                className="text-[15px]! md:text-[15px]!"
              />
              <p className="text-xs text-muted-foreground">
                Plain English. Include what you want, or exclude what you don&rsquo;t. Optional. Blank means &ldquo;All&rdquo;.
              </p>
            </div>
            <InvestorTypesPicker
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              onAdd={(tokens) => {
                if (tokens.length === 0) return;
                const current = investorTypesRequest.trim();
                const existing = new Set(
                  current
                    .split(",")
                    .map((s) => s.trim().toLowerCase())
                    .filter(Boolean),
                );
                const additions = tokens.filter((t) => !existing.has(t.toLowerCase()));
                if (additions.length === 0) {
                  typesInputRef.current?.focus();
                  return;
                }
                const next = current
                  ? `${current}, ${additions.join(", ")}`
                  : additions.join(", ");
                setInvestorTypesRequest(next.slice(0, 500));
                setTimeout(() => typesInputRef.current?.focus(), 0);
              }}
            />
            <div className="space-y-2 border-t border-brand-line pt-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                About you
              </h3>
              <p className="text-xs text-muted-foreground">
                We use this to personalise every outbound draft.
              </p>
              <div className="rounded-lg border border-brand-line bg-brand-paper-2/60 shadow-sm">
                <div className="px-4">
                  <Field
                    label="First name *"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jane"
                  />
                  <div className="flex flex-col gap-1.5 border-b border-brand-line py-3 last:border-b-0">
                    <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Role *
                    </label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-foreground shadow-none focus:ring-0 focus-visible:ring-0 data-[placeholder]:text-muted-foreground/70">
                        <SelectValue placeholder="Select your role…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Banker representing the issuer">
                          Banker representing the issuer
                        </SelectItem>
                        <SelectItem value="Founder/CEO/GP raising directly">
                          Founder / CEO / GP raising directly
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Field
                    label="Firm name"
                    value={firm}
                    onChange={(e) => setFirm(e.target.value)}
                    placeholder="Optional"
                  />
                  <Field
                    label="Email *"
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    placeholder="you@example.com"
                    error={
                      userEmail.trim().length > 0 &&
                      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.trim())
                        ? "Please enter a valid email address."
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2.5 border-t border-brand-line pt-4">
              <Checkbox
                id="deal-draft-outreach"
                checked={draftOutreach}
                onCheckedChange={(v) => setDraftOutreach(v === true)}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="deal-draft-outreach" className="text-sm font-medium text-foreground">
                  Draft personalized outreach for each contact
                </Label>
                <p className="text-xs text-muted-foreground">
                  Leave this on to get an email and a LinkedIn message drafted for
                  every contact. Turn it off to receive contacts and ratings only.
                  The price is the same either way, and this applies to this delivery
                  only.
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Excluded firms are managed in the Excluded Firms section below and apply to this delivery.
            </p>
            {startError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {startError}
              </div>
            )}
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                onClick={handleBuildOutreach}
                disabled={
                  startBusy ||
                  pipelineInFlight ||
                  !firstName.trim() ||
                  !role.trim() ||
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.trim()) ||
                  !(
                    Number.isInteger(Number(deliveryTarget)) &&
                    Number(deliveryTarget) >= 10 &&
                    Number(deliveryTarget) <= 1000
                  )
                }
              >
                {startBusy ? "Starting…" : "Build outreach"}
              </Button>
              <button
                type="button"
                onClick={() => setContactsPanelOpen(false)}
                className="text-sm text-muted-foreground underline hover:text-foreground"
              >
                Cancel
              </button>
            </div>
            {pipelineInFlight && (
              <p className="text-xs text-muted-foreground">
                A delivery is already running on this deal. You can start another once it finishes.
              </p>
            )}
          </section>
        )}
        </>
      )}
      <section className="space-y-3 rounded-xl border border-brand-line bg-brand-paper p-4">
          <h2 className="text-[15px] font-semibold uppercase tracking-wider text-brand-muted">Refine deal plan</h2>
          <p className="text-xs text-brand-muted">
            Updates this deal&rsquo;s saved plan so future continues inherit your changes.
            Existing results are not affected.
          </p>

          {isDirectEquityMezz && planJson ? (
            <RaiseSummary plan={planJson as { dealSummary?: string }} />
          ) : null}
          {isDirectEquityMezz && planJson ? (
            <PlanTagSummary plan={planJson as Parameters<typeof PlanTagSummary>[0]["plan"]} />
          ) : null}
          {isFund && planJson ? (
            <RaiseSummary plan={planJson as { dealSummary?: string }} />
          ) : null}
          {isLoans && planJson ? (
            <RaiseSummary plan={planJson as { dealSummary?: string }} />
          ) : null}
          {isRealEstate && planJson ? (
            <RaiseSummary plan={planJson as { dealSummary?: string }} />
          ) : null}

          {parseReport.length > 0 && (
            <div className="space-y-1.5 rounded-md border border-brand-line/60 bg-brand-paper-2/30 px-3 py-2.5">
              <div className="text-[12px] font-semibold uppercase tracking-wider text-brand-muted">
                Current materials
              </div>
              <ul className="space-y-1">
                {(["presentation", "memo", "template"] as MaterialCategory[]).map((cat) => {
                  const cur = currentBySlot(cat);
                  const queuedRemove = removals.has(cat);
                  return (
                    <li
                      key={cat}
                      className="flex items-center justify-between gap-3 text-[14px]"
                    >
                      <span className="min-w-0 truncate text-brand-ink">
                        <span className="text-brand-muted">{SLOT_LABELS[cat]}:</span>{" "}
                        {cur ? (
                          <span
                            className={
                              queuedRemove
                                ? "line-through text-brand-muted"
                                : "text-brand-ink"
                            }
                          >
                            {cur.filename}
                          </span>
                        ) : (
                          <span className="italic text-brand-muted">none</span>
                        )}
                      </span>
                      {cur && (
                        <button
                          type="button"
                          onClick={() => toggleRemoval(cat)}
                          className="text-[12px] text-brand-muted underline hover:text-brand-ink"
                        >
                          {queuedRemove ? "Undo remove" : "Remove"}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {parseReport.length === 0 && (deal as { description?: string | null }).description && (
            <p className="text-xs text-brand-muted">
              Deal context on file: &ldquo;{String((deal as { description?: string | null }).description).slice(0, 80)}
              {String((deal as { description?: string | null }).description).length > 80 ? "..." : ""}&rdquo;
            </p>
          )}

          <div className="space-y-3">
            {(["presentation", "memo", "template"] as MaterialCategory[]).map((cat) => (
              <FileField
                key={cat}
                label={
                  currentBySlot(cat)
                    ? `Replace ${SLOT_LABELS[cat].toLowerCase()}`
                    : SLOT_LABELS[cat]
                }
                hint="PDF or DOCX · optional"
                accept=".pdf,.docx"
                value={stagedFile(cat)}
                onChange={(f) => {
                  setStagedFile(cat, f);
                  // Staging a replacement supersedes a queued removal.
                  if (f && removals.has(cat)) {
                    setRemovals((prev) => {
                      const next = new Set(prev);
                      next.delete(cat);
                      return next;
                    });
                  }
                }}
              />
            ))}
          </div>

          <div className="space-y-2 rounded-md border border-brand-line/60 bg-brand-paper-2/30 px-3 py-3">
            <div className="text-[12px] font-semibold uppercase tracking-wider text-brand-muted">
              Company financials (optional)
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="deal-revenue-musd" className="text-xs text-brand-muted">
                  Revenue ($M)
                </Label>
                <Input
                  id="deal-revenue-musd"
                  inputMode="decimal"
                  value={revenueM}
                  onChange={(e) =>
                    setRevenueM(e.target.value.replace(/[^0-9.]/g, ""))
                  }
                  placeholder="e.g. 12.5"
                  className="text-[15px]! md:text-[15px]!"
                />
                {interpretedMusdLine(revenueM) && (
                  <p className="text-xs text-brand-muted">
                    {interpretedMusdLine(revenueM)}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="deal-ebitda-musd" className="text-xs text-brand-muted">
                  EBITDA ($M)
                </Label>
                <Input
                  id="deal-ebitda-musd"
                  inputMode="decimal"
                  value={ebitdaM}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9.-]/g, "");
                    setEbitdaM(
                      v.startsWith("-")
                        ? `-${v.slice(1).replace(/-/g, "")}`
                        : v.replace(/-/g, ""),
                    );
                  }}
                  placeholder="e.g. -1.2"
                  className="text-[15px]! md:text-[15px]!"
                />
                {interpretedMusdLine(ebitdaM) && (
                  <p className="text-xs text-brand-muted">
                    {interpretedMusdLine(ebitdaM)}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor="deal-premoney-musd"
                  className="text-xs text-brand-muted"
                >
                  Target pre-money valuation ($M)
                </Label>
                <Input
                  id="deal-premoney-musd"
                  inputMode="decimal"
                  value={premoneyM}
                  onChange={(e) =>
                    setPremoneyM(e.target.value.replace(/[^0-9.]/g, ""))
                  }
                  placeholder="e.g. 40"
                  className="text-[15px]! md:text-[15px]!"
                />
                {interpretedMusdLine(premoneyM) && (
                  <p className="text-xs text-brand-muted">
                    {interpretedMusdLine(premoneyM)}
                  </p>
                )}
              </div>

            </div>
            <p className="text-xs text-brand-muted">
              Used by the Market Comps and Valuation Report to place your numbers
              against the market. Optional.
            </p>
          </div>

          {isDirectEquityMezz && (
            <div className="space-y-3">
              <TagPicker
                label="Industry Types"
                value={pickIndustryTypes}
                options={vocab?.industryTypes ?? []}
                onChange={setPickIndustryTypes}
              />
              <TagPicker
                label="Company Types"
                value={pickCompanyTypes}
                options={vocab?.companyTypes ?? []}
                onChange={setPickCompanyTypes}
              />
              <TagPicker
                label="Vertical Types"
                value={pickVerticalTypes}
                options={vocab?.verticalTypes ?? []}
                onChange={setPickVerticalTypes}
              />
              <TagPicker
                label="Stage Types"
                value={pickStageTypes}
                options={vocab?.stageTypes ?? []}
                onChange={setPickStageTypes}
              />
            </div>
          )}

          {isFund && (
            <LpTargetSelector
              value={lpTargetsPick}
              onChange={setLpTargetsPick}
            />
          )}

          {isLoans && (
            <div className="space-y-3">
              <LoanTargetingLadder
                checked={loanChecked}
                onChange={setLoanChecked}
                onReset={() => setLoanChecked(loanSeedIds)}
                canReset={loanTargetsDirty}
                resetLabel="Reset changes"
              />
              <LoanTargetSelector
                checked={loanChecked}
                onChange={setLoanChecked}
              />
            </div>
          )}

          {isRealEstate && (
            <ReTargetSelector
              propertyTypes={rePropertyTypes}
              onPropertyTypesChange={setRePropertyTypes}
              propertyVocab={vocab?.realEstatePropertyTypes ?? []}
              capitalTypes={reCapitalTypes}
              onCapitalTypesChange={setReCapitalTypes}
            />
          )}

          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] leading-[1.6]"
            placeholder="e.g., Focus on growth equity, not venture. Exclude Series A funds."
          />
          {planError && <div className="text-sm text-destructive">{planError}</div>}
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!canSubmit}
              onClick={handleUpdatePlan}
            >
              {planBusy
                ? materialsChanged
                  ? "Re-reading materials…"
                  : "Updating…"
                : "Update plan"}
            </Button>
            {materialsChanged && !planBusy && (
              <span className="self-center text-[13px] text-brand-muted">
                Materials changed — plan will be regenerated.
              </span>
            )}
          </div>
      </section>
      <DealClassificationSection dealId={dealId} deal={deal as never} />
      <DealIncludedDomains dealId={dealId} />
      <DealExcludedDomains dealId={dealId} />
      <DealHqLocationSection dealId={dealId} />
      {(pipelineInFlight || completionNote) && (
        <PipelineBanner
          latestRun={latestRun}
          liveStage={liveStage}
          completionNote={completionNote}
          inFlight={pipelineInFlight}
        />
      )}
    </>
  );

}

function PipelineBanner({
  latestRun,
  liveStage,
  completionNote,
  inFlight,
}: {
  latestRun: {
    id: string;
    status: string;
    current_stage: number | null;
    delivery_target: number | null;
    created_at: string;
    error_message: string | null;
  } | undefined;
  liveStage: number | null;
  completionNote:
    | { kind: "complete"; delivered: number | null }
    | { kind: "failed"; message: string }
    | null;
  inFlight: boolean;
}) {
  if (completionNote?.kind === "complete") {
    const n = completionNote.delivered;
    return (
      <section
        role="status"
        aria-live="polite"
        className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
      >
        {n !== null ? (
          <>
            <span className="font-medium">Delivery complete:</span> {n} new
            contacts added.
          </>
        ) : (
          <span className="font-medium">Delivery complete.</span>
        )}
      </section>
    );
  }
  if (completionNote?.kind === "failed") {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-brand-ink"
      >
        <div className="font-medium text-destructive">The pipeline stopped before completing.</div>
        <p className="mt-1 text-brand-muted">{completionNote.message}</p>
        <p className="mt-1 text-brand-muted">
          Use the Get more contacts button above to start a new delivery.
        </p>
      </section>
    );
  }
  if (!inFlight || !latestRun) return null;

  const stageNum = liveStage ?? latestRun.current_stage;
  const stageLabel =
    (typeof stageNum === "number" && STAGE_NAME[stageNum]) || "Preparing";
  const startMs = new Date(latestRun.created_at).getTime();
  const started = new Date(startMs);
  const startedLabel = started.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  const band = estimateRunMinutes(
    typeof latestRun.delivery_target === "number" && latestRun.delivery_target > 0
      ? latestRun.delivery_target
      : 100,
  );
  const elapsedMin = Math.max(0, Math.floor((Date.now() - startMs) / 60000));
  return (
    <section
      role="status"
      aria-live="polite"
      className="rounded-xl border border-brand-line bg-brand-paper-2/60 px-4 py-3 text-sm text-brand-ink"
    >
      <div className="font-medium">
        Delivery in progress: {stageLabel}.
      </div>
      <p className="mt-1 text-brand-muted">
        Started {startedLabel}, expected about {band.lo} to {band.hi} minutes
        ({elapsedMin} min elapsed). The investor list updates automatically
        when the delivery completes.
      </p>
    </section>
  );
}